// Commerce Types - Consolidated TypeScript definitions
// Created On: 2025-07-22
// Last Modified: 2025-07-22

// Feature Types
export type Feature = 'PRODUCT' | 'FEE' | 'COUPONS' | 'POS'

// Payment Types
export type PaymentType = 'CreditCard' | 'Ach' | 'None' | 'Cash' | 'Check'
export type PaymentSubType =
  | 'Visa'
  | 'MasterCard'
  | 'AMEX'
  | 'Discover'
  | 'DinersClub'
  | 'JCB'
  | 'Check'
  | 'Cash'
  | 'Checking'
  | 'Savings'
  | 'Other'

// Recurring Payment Types
export type RecurringFrequency = undefined | 'low-balance' | 'weekly' | 'bi-weekly' | 'monthly'

// Product Types
export interface Product {
  id: number
  name: string
  imageUrl: string
  currency: string
  price: number
  salePrice?: number
}

// Promo Code Types
export interface LegacyPromoCode {
  id: number
  codeName: string
  discountAmt: number
  isPercentage: boolean
}

// Seat Information
export interface SeatInfo {
  seat: string
  row: string
  section: string
  isDistancing: boolean
}

// Cart Item Types
export interface CartItemMeta {
  skyward?: {
    feeId?: number
    studentId: number
    studentName: string
    tenantId: number | null
    amount: number
    feeDescription?: string
    feeName: string
    fmBcId?: string
  }
  recurring?: {
    frequency: string
    data: string
    amount: number
  }
}
export const ItemTypes = {
  SkywardFee: 1,
  Fee: 2,
  GOOD: 3,
  GENERAL_ADMISSION: 4,
  RESERVED_SEATING: 5,
  SEASON_PASS: 6,
  SkywardFoodService: 7, // Skyward fee payment
}
export interface CartItem {
  id: number
  itemId: number
  itemTypeId: number
  itemType: string
  itemAmount: number
  name: string
  image?: string
  description: string | null
  qty: number
  cartId: number
  tenantId: number
  schoolId?: number
  departmentId?: number
  amount: number
  discount: number
  tax: number
  taxPercentage: number
  taxable: boolean
  subtotal: number
  fee: number
  meta?: CartItemMeta
}

export interface CartItemLegacy {
  id: number
  integrationId?: number
  integrationTypeId?: number
  itemType: number
  studentName?: string
  itemName: string
  name?: string
  itemDescription?: string
  itemQuantity: number
  itemExpires?: Date
  expireTime?: null
  schoolName: string
  feeId: number
  fee_bcId: string
  studentId?: number
  schoolId: number
  schoolEmail: string
  schoolPhone: string
  amount: number
  paymentInfoString?: string
  feeAmount: number
  productId?: number
  taxes: number
  parentItemName?: string
  parentItemId?: number
  recurringString: string
  seatId: null
  seatInfo: null
  promos: LegacyPromoCode[]
  isPosOnly: boolean
  isAdminProduct: boolean
  PONum: string
  hasCustomFields: boolean
  cartKey: string
  redemptionId?: number
  allowECheck: boolean
  isRecurring: boolean
  recurringDate?: string
  recurring: '' | ''
}

export interface AddCartItem {
  integrationId?: number
  feeId?: number
  fee_bcId?: number
  name: string
  description: string
  studentId?: number | string
  schoolId: number
  amount: number
  itemType: number
  cartKey: string
  studentName?: string
  recurringFrequency?: string
  firstRecurDate?: string
  lowBalanceRecur?: number
}

// Merchant Types
export interface MerchantInfo {
  tenantId: number
  name: string
  logo: string
  scope: Record<string, unknown> | null
  merchantProfileId: number
  merchantAccountId: number
  schoolId: number
  departmentId: number
  paymentType: PaymentType
  itemType: boolean
  merchantPays: number
  feePerTransaction: number
  feePerItem: number
  percentPerTransaction: number
  taxPercentage: number
}

export interface Merchant {
  tenantId: number
  schoolId: number
  departmentId: number
  name: string
  logo: string
  scope: Record<string, unknown> | null
  merchantProfileId: number
  merchantAccountId: number
  allowAch: boolean
  achTransactionFee: number
  achTransactionPercentageFee: number
  achItemFee: number
  creditCardTransactionFee: number
  creditCardTransactionPercentageFee: number
  creditCardItemFee: number
  merchantPaysFees: boolean
  merchantPaysAchFees: boolean
  taxPercentage: number
}

export interface Rate {
  paymentMethodType: string
  merchantPays: boolean
  feePerTransaction: number
  feePerItem: number
  percentPerTransaction: number
  taxPercentage: number
}

// Cart Types
export interface Cart {
  id: number
  sessionId?: string
  key: string
  merchant?: Merchant
  rate: Rate
  tenantId: number
  schoolId?: number
  departmentId?: number
  items: CartItem[]
  paymentType?: string
  paymentSubType?: string
  total: number
  tax: number
  subtotal: number
  totalItems: number
  fees: number
  discount: number
}

export interface CartTotals {
  total: number
  fees: number
  merchantPays: boolean
}

// Order Types
export interface Order {
  amount: number
  feeAmount: number
  frequency: RecurringFrequency
  id: number
  nextDate: string
  student: string
}

// Recurring Payment Types
export interface RecurringFoodPayment {
  amount: number
  feeAmount: number
  frequency: RecurringFrequency
  id: number
  nextDate: string
  student: string
  studentId: string
  threshold: number
}

export interface RecurringPayment {
  amount: number
  feeAmount: number
  frequency: RecurringFrequency
  id: number
  nextDate: string
  student: string
}

// Payment Method Types
export interface AddPaymentMethodPayload {
  paymentMethod: PaymentType
  paymentSubMethod: PaymentSubType
  total: number
  fees: number
  merchantId: string
  id: string
  cardholderName: string
  obfuscatedAccountNumber: string
  expireDate?: string
  address1?: string
  address2?: string
  city?: string
  state?: string
  postalCode: string
  country?: string
  bankName?: string
  isCC: boolean
}

export interface UpdateCartPaymentMethodRequest {
  name: string
  email?: string | null
  phoneNumber?: string | null
  sessionId?: string | null
  payerId?: string | null
  paymentMethodId?: string | null
  obfuscatedAccountNumber?: string | null
  expireDate?: string | null
  paymentType: PaymentType
  paymentSubType: PaymentSubType
  city?: string | null
  state?: string | null
  postalCode?: string | null
  total: number
  fees: number
  merchantId: number
  id: number
  key: string
  reCaptchaToken?: string
}

// ProPay Response Types
export interface ProPaySeamlessResponseCipher {
  Action: string
  ErrCode?: string
  ErrMsg?: string
  echo?: string
  ProcessResult?: string
  ProcessResultResultCode?: string
  ProcessResultResultMessage?: string
  ProcErrCode?: string
  ProcErrMsg?: string
  StoreErrCode?: string
  StoreErrMsg?: string
  ProcessResultAuthorizationCode?: string
  ProcessResultCvvCode?: string
  ProcessResultAVSCode?: string
  PayerID?: string
  PaymentMethodId?: string
  CardholderName?: string
  ObfuscatedAccountNumber?: string
  ExpireDate?: string
  Address1?: string
  Address2?: string
  Address3?: string
  City?: string
  State?: string
  PostalCode?: string
  Country?: string
  BankName?: string
  ProcessResultTransactionHistoryID?: string
  ProcessResultTransactionId?: string
  GrossAmt?: string
  NetAmt?: string
  PerTransFee?: string
  Rate?: string
  GrossAmtLessNetAmt?: string
}

// State Types
export interface CartState {
  cartItems: CartItem[]
  setCartItems: (items: CartItem[]) => void
  removeItemFromCart: (id: number) => Promise<boolean>
  addItemToCart: (item: AddCartItem) => Promise<CartItem[]>
  refreshCart: () => void
}

export interface OrdersState {
  orders: Order[]
  fetchOrders: () => void
}

export interface RecurringPaymentsState {
  recurringPayments: RecurringPayment[]
  fetchRecurringPayments: () => void
  isRecurringPaymentsLoading: boolean
  recurringPaymentsError?: Error | null
}

// Action Types
export type CartAction = 'remove-cart-item' | 'add-cart-item' | 'cart-items'

// Helper Functions Types (for reference)
export interface CartProperties {
  cartItems: CartItem[]
  cartTotal: number
  cartTotalItems: number
  cartHasSkyward: boolean
  cartHasFeePayment: boolean
  cartHasRecurring: boolean
  cartHasFoodPayment: boolean
}

// Error Types
export class CartError extends Error {
  isCartNotFound?: boolean
  isMaxErrorsReached?: boolean
  consecutiveErrors?: number

  constructor(
    message: string,
    options?: {
      isCartNotFound?: boolean
      isMaxErrorsReached?: boolean
      consecutiveErrors?: number
    },
  ) {
    super(message)
    this.name = 'CartError'
    if (options) {
      this.isCartNotFound = options.isCartNotFound
      this.isMaxErrorsReached = options.isMaxErrorsReached
      this.consecutiveErrors = options.consecutiveErrors
    }
  }
}
